import type { EnsureSessionOptions, EnsureSessionReason, SessionFallbackMode } from './types';

export const ID_TOKEN_RETRY_INTERVAL_MS = 100;
export const AUTH0_STATE_POLL_INTERVAL_MS = 50;
export const AUTH_CALLBACK_RETRY_DELAY_MS = 250;
export const AUTH_CALLBACK_MAX_ATTEMPTS = 3;
export const AUTH_CALLBACK_ORGANIZATION_SCOPE_TIMEOUT_MS = 2_000;
export const AUTH0_SESSION_TIMEOUT_ERROR_MESSAGE = 'Auth0 session sync timed out';

type NormalizedEnsureSessionOptions = {
  reason: EnsureSessionReason;
  refresh: boolean;
  freshAuth0Token: boolean;
  targetOrganizationId: string | null;
  fallbackMode: SessionFallbackMode;
  suppressMfaRedirect: boolean;
};

export const withTimeout = async <T>(operation: Promise<T>, timeoutMs?: number): Promise<T> => {
  if (!timeoutMs) {
    return operation;
  }

  return await Promise.race<T>([
    operation,
    new Promise<T>((_, reject) => {
      setTimeout(() => {
        reject(new Error(AUTH0_SESSION_TIMEOUT_ERROR_MESSAGE));
      }, timeoutMs);
    }),
  ]);
};

export const sleep = async (delayMs: number) => {
  await new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
};

export const isSessionSyncTimeoutError = (error: unknown): error is Error => {
  return (
    error instanceof Error &&
    (error.message === AUTH0_SESSION_TIMEOUT_ERROR_MESSAGE ||
      error.message === 'Auth0 SDK did not settle in time')
  );
};

export const isMissingIdTokenError = (error: unknown): error is Error => {
  return error instanceof Error && error.message === 'Missing ID token';
};

export const isMissingOrganizationScopedAccessTokenError = (error: unknown): error is Error => {
  return error instanceof Error && error.message === 'Missing organization-scoped access token';
};

const normalizeEnsureSessionOptions = (
  options?: EnsureSessionOptions,
): NormalizedEnsureSessionOptions => {
  return {
    reason: options?.reason ?? 'request',
    refresh: options?.refresh ?? false,
    freshAuth0Token: options?.freshAuth0Token ?? false,
    targetOrganizationId: options?.targetOrganizationId ?? null,
    fallbackMode: options?.fallbackMode ?? 'clear-auth',
    suppressMfaRedirect: options?.suppressMfaRedirect ?? false,
  };
};

export const createSessionSyncKey = (options?: EnsureSessionOptions) => {
  return JSON.stringify(normalizeEnsureSessionOptions(options));
};

export const getOrganizationScopeTimeoutMs = (options?: EnsureSessionOptions) => {
  if (options?.reason !== 'auth-callback' || !options.timeoutMs) {
    return options?.timeoutMs;
  }

  return Math.min(options.timeoutMs, AUTH_CALLBACK_ORGANIZATION_SCOPE_TIMEOUT_MS);
};

export const shouldTreatOrganizationBootstrapFailureAsNonFatal = (
  error: unknown,
  options?: EnsureSessionOptions,
) => {
  return (
    options?.reason === 'auth-callback' &&
    (isMissingIdTokenError(error) ||
      isMissingOrganizationScopedAccessTokenError(error) ||
      isSessionSyncTimeoutError(error))
  );
};

export const isUnauthenticatedAuth0Error = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const auth0Error = error as { error?: unknown; message?: unknown };
  const errorCode = typeof auth0Error.error === 'string' ? auth0Error.error : null;

  return (
    errorCode === 'login_required' ||
    errorCode === 'consent_required' ||
    errorCode === 'interaction_required' ||
    errorCode === 'missing_refresh_token' ||
    errorCode === 'invalid_grant' ||
    auth0Error.message === 'Login required'
  );
};

const getAuth0ErrorStatus = (error: Record<string, unknown>): number | null => {
  const directStatus =
    typeof error.status === 'number'
      ? error.status
      : typeof error.statusCode === 'number'
        ? error.statusCode
        : null;

  if (directStatus !== null) {
    return directStatus;
  }

  const response =
    typeof error.response === 'object' && error.response !== null
      ? (error.response as Record<string, unknown>)
      : null;

  if (!response) {
    return null;
  }

  if (typeof response.status === 'number') {
    return response.status;
  }

  return typeof response.statusCode === 'number' ? response.statusCode : null;
};

const getAuth0ErrorCode = (error: Record<string, unknown>): string | null => {
  if (typeof error.error === 'string') {
    return error.error;
  }

  const response =
    typeof error.response === 'object' && error.response !== null
      ? (error.response as Record<string, unknown>)
      : null;
  const responseData =
    response && typeof response.data === 'object' && response.data !== null
      ? (response.data as Record<string, unknown>)
      : null;

  return typeof responseData?.error === 'string' ? responseData.error : null;
};

const normalizeErrorValue = (value: string) => value.trim().toLowerCase().replace(/[_-]/g, ' ');

const getAuth0ErrorMessages = (error: Record<string, unknown>): string[] => {
  const messages: string[] = [];

  if (typeof error.message === 'string') {
    messages.push(error.message);
  }

  if (typeof error.error_description === 'string') {
    messages.push(error.error_description);
  }

  const response =
    typeof error.response === 'object' && error.response !== null
      ? (error.response as Record<string, unknown>)
      : null;
  const responseData =
    response && typeof response.data === 'object' && response.data !== null
      ? (response.data as Record<string, unknown>)
      : null;

  if (typeof responseData?.error === 'string') {
    messages.push(responseData.error);
  }

  if (typeof responseData?.error_description === 'string') {
    messages.push(responseData.error_description);
  }

  if (typeof responseData?.message === 'string') {
    messages.push(responseData.message);
  }

  const nestedError =
    responseData && typeof responseData.error === 'object' && responseData.error !== null
      ? (responseData.error as Record<string, unknown>)
      : null;

  if (typeof nestedError?.message === 'string') {
    messages.push(nestedError.message);
  }

  return [...new Set(messages)];
};

const hasMfaRequiredAuth0ErrorName = (error: Record<string, unknown>) => {
  if (typeof error.name !== 'string') {
    return false;
  }

  return normalizeErrorValue(error.name).replace(/\s+/g, '') === 'mfarequirederror';
};

const hasMfaRequiredAuth0Message = (error: Record<string, unknown>) => {
  return getAuth0ErrorMessages(error).some((message) => {
    const normalizedMessage = normalizeErrorValue(message);

    return (
      normalizedMessage.includes('mfa required') ||
      normalizedMessage.includes('multifactor authentication required')
    );
  });
};

export const isMfaRequiredAuth0Error = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const auth0Error = error as Record<string, unknown>;
  const errorCode = getAuth0ErrorCode(auth0Error);

  if (errorCode === 'mfa_required' || hasMfaRequiredAuth0ErrorName(auth0Error)) {
    return true;
  }

  if (!hasMfaRequiredAuth0Message(auth0Error)) {
    return false;
  }

  const errorStatus = getAuth0ErrorStatus(auth0Error);

  return errorStatus === null || errorStatus === 403;
};
